import {observer} from "mobx-react";
import FontBody1 from "../../../components/fonts/font-body1/font-body1";
import FontBody2 from "../../../components/fonts/font-body2/font-body2";
import {FONT_COLOR} from "../../../components/fonts/font-props";
import IconWarning from "../../../components/icons/icon-warning/icon-warning";
import Icon from "../../../components/icons/icon/icon";
import {BUTTON_VARIANTS} from "../../../components/inputs/button-variants";
import {IButtonProps} from "../../../components/inputs/button/button";
import {ActionResultModal} from "../../../components/modal/components/action-result-modal/action-result-modal";
import CreateRmaResponseStore from "../stores/create-rma-response-store";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import CreateRmaMainStoreInstanceManager, { CreateRmaMainStore } from "../stores/create-rma-main-store";
import {
    isSubmissionCredentialsDeclined,
    RUN_VARIANT,
    SUBMISSION_ERROR,
} from "../stores/create-rma-submission-types";
import config from "../../../config";
import './create-rma-response-modal.scss';

interface IProps {
    store: CreateRmaResponseStore,
    createRmaMainStore: CreateRmaMainStore
}

const CreateRmaResponseModal: React.FC<IProps> = observer(({store, createRmaMainStore}) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';
    const variant = store.variant;
    const isPartial = variant === RUN_VARIANT.PARTIAL;
    const isSingleOutcome = store.submittedCount === 1;

    const getShipmentDescriptor = (draftIndex: number, firstItemName?: string): string => {
        const itemName = firstItemName?.trim() || getTranslation('ITEM', pageScope);
        return getTranslation('RESPONSE_ROW_SHIPMENT', pageScope, [
            String(draftIndex),
            itemName,
        ]);
    };

    const getRowOutcomeLabel = (isCreated: boolean): string => {
        if (variant === RUN_VARIANT.NONE_CREATED) {
            return getTranslation('RESPONSE_ROW_NOT_CREATED', pageScope);
        }

        return isCreated
            ? getTranslation('RESPONSE_ROW_CREATED', pageScope)
            : getTranslation('RESPONSE_ROW_FAILED', pageScope);
    };

    /**
     * The internal markers carry no merchant-facing copy: the balance block has its own translation
     * and the remaining two are covered by the generic row label.
     */
    const getRowErrorMessage = (errorMessage?: string): string | null => {
        if (!errorMessage || isSubmissionCredentialsDeclined(errorMessage)) {
            return null;
        }

        if (errorMessage === SUBMISSION_ERROR.CUSTOMER_PAYMENT_REQUIRED) {
            return getTranslation('BLOCK_PAID_RMA_TOOLTIP', pageScope);
        }

        if (errorMessage === SUBMISSION_ERROR.SUBMISSION_FAILED) {
            return null;
        }

        return errorMessage;
    };

    const title = (() => {
        /** the server's own copy wins for a one-outcome run, success or failure (AC1) */
        if (isSingleOutcome && store.messageTitle) {
            return store.messageTitle;
        }

        if (variant === RUN_VARIANT.ALL_CREATED && isSingleOutcome) {
            const rmaLabel = store.rmaId ? `RMA${store.rmaId}` : getTranslation('CREATE_RMA', pageScope);
            return getTranslation('MESSAGE_SUCCESS_TITLE', pageScope, [rmaLabel]);
        }

        if (variant === RUN_VARIANT.ALL_CREATED) {
            return getTranslation('N_RMAS_CREATED_SUCCESSFULLY', pageScope, [String(store.createdCount)]);
        }

        if (variant === RUN_VARIANT.PARTIAL) {
            return getTranslation('M_OF_N_RMAS_CREATED', pageScope, [
                String(store.createdCount),
                String(store.submittedCount),
            ]);
        }

        return getTranslation('COULD_NOT_CREATE_RMAS', pageScope);
    })();

    const bodyMessage = (() => {
        if (!isSingleOutcome) {
            return null;
        }

        return store.message || getRowErrorMessage(store.rows[0]?.errorMessage);
    })();

    const content = (
        <div className="crma-api-response-modal">
            {bodyMessage && (
                <FontBody1>
                    {bodyMessage}
                </FontBody1>
            )}
            <ul className="crma-api-response-modal-rows">
                {store.rows.map((row) => (
                    <li key={row.draftIndex}>
                        <FontBody2 color={FONT_COLOR.PRIMARY3}>
                            {variant === RUN_VARIANT.ALL_CREATED ? (
                                <>
                                    {row.isCreated && row.rmaId ? (
                                        <a
                                            className="crma-api-response-modal-rma-link"
                                            href={`${config.apiUrl}/dashboard/returns?rmaid=${row.rmaId}`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            RMA{row.rmaId}
                                        </a>
                                    ) : (
                                        <span>RMA</span>
                                    )}
                                    {' — '}
                                    {getShipmentDescriptor(row.draftIndex, row.firstItemName)}
                                </>
                            ) : (
                                <>
                                    {getShipmentDescriptor(row.draftIndex, row.firstItemName)}
                                    {' '}
                                    {getRowOutcomeLabel(row.isCreated)}
                                </>
                            )}
                        </FontBody2>
                        {!isSingleOutcome && getRowErrorMessage(row.errorMessage) && (
                            <div className="crma-api-response-modal-row-error">
                                <FontBody2 color={FONT_COLOR.PRIMARY3}>
                                    {getRowErrorMessage(row.errorMessage)}
                                </FontBody2>
                            </div>
                        )}
                    </li>
                ))}
            </ul>
            {store.warningMessage && (
                <div className="warning-message">
                    <Icon Component={IconWarning} color={'var( --color-accent-4)'} />
                    <FontBody1 color={FONT_COLOR.ACCENT4}>
                        {store.warningMessage}
                    </FontBody1>
                </div>
            )}
            {isPartial && (
                <div className="warning-message">
                    <Icon Component={IconWarning} color={'var( --color-accent-4)'} />
                    <FontBody1 color={FONT_COLOR.ACCENT4}>
                        {getTranslation('PARTIAL_RMAS_CREATED_WARNING', pageScope)}
                    </FontBody1>
                </div>
            )}
        </div>
    );

    let buttons: IButtonProps[] = [];

    if (variant === RUN_VARIANT.ALL_CREATED) {
        buttons = [
            {
                variant: BUTTON_VARIANTS.Transparent,
                text: getTranslation('CREATE_NEW', pageScope),
                onClick: () => {
                    store.abortTimer();
                    CreateRmaMainStoreInstanceManager.voidInstance();
                    createRmaMainStore.hideResponseModal();
                },
            },
            {
                variant: BUTTON_VARIANTS.PrimaryAccent,
                text: getTranslation('VIEW', pageScope) + (store.hasCountdown ? store.getTimer() : ''),
                onClick: () => {
                    store.abortTimer();
                    store.openCreatedRma();
                },
            },
        ];
    } else if (variant === RUN_VARIANT.PARTIAL) {
        buttons = [
            {
                variant: BUTTON_VARIANTS.Transparent,
                text: getTranslation('VIEW_RMA', pageScope),
                onClick: () => {
                    store.openCreatedRma();
                },
            },
            {
                variant: BUTTON_VARIANTS.PrimaryAccent,
                text: getTranslation('RETRY_FAILED', pageScope),
                onClick: () => {
                    createRmaMainStore.hideResponseModal();
                },
            },
        ];
    } else {
        buttons = [
            {
                variant: BUTTON_VARIANTS.Dismiss,
                text: getTranslation('TRY_AGAIN', pageScope),
                onClick: () => {
                    createRmaMainStore.hideResponseModal();
                },
            },
        ];
    }

    return (
        <ActionResultModal
            buttons={buttons}
            onClose={() => {}}
            isClosedDisabled={true}
            title={title}
            content={content}
            success={store.success}
        />
    );
});

export default CreateRmaResponseModal;
